import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.tsx'
import './index.css'
import i18n from './i18n/config'
import { installDensityShortcut, readDensity, writeDensity } from './hooks/use-density'
import { installThemeShortcut, readTheme, writeTheme } from './hooks/use-theme'
import { initSentry } from './lib/sentry'
import { ErrorBoundary, DefaultFallback } from './components/ErrorBoundary'
import { initVersionCheck } from './utils/versionCheck'

// Sentry is lazy-loaded only for authenticated routes — public landing /
// SEO visitors never download the SDK (~100KB gzip). The init runs after
// first paint via idle callback so it never blocks rendering.
const isAuthRoute = /^\/(?:dashboard|onboarding|account|technician-info|pay\/)/.test(
  window.location.pathname
);
if (isAuthRoute) {
  const start = () => { void initSentry(); };
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(start);
  } else {
    setTimeout(start, 0);
  }
}

// Apply stored density + theme before React mounts (avoids flicker)
writeDensity(readDensity())
writeTheme(readTheme())
installDensityShortcut()
installThemeShortcut()

// Self-heal stale cached SPAs: reload onto the latest deploy when one ships.
initVersionCheck()

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary fallback={(props) => <DefaultFallback {...props} />}>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </ErrorBoundary>
);

// Signal the prerenderer (vite.config.ts renderAfterDocumentEvent) that the
// page is ready to snapshot. The old double-RAF fired right after mount —
// BEFORE lazy routes resolved their chunk and BEFORE i18n namespaces
// arrived — so every lazy SEO page (blog posts, /pricing, city pages, …)
// was captured as an empty shell with the default <title>, and pages that
// did render were captured mid-translation with raw keys ("meta.title").
// Instead, poll until the page is actually done:
//   1. #root has children and the Suspense RouteLoader fallback is gone
//      (route chunk + suspended namespaces resolved, blog body fetched);
//   2. i18next has no in-flight backend loads (locale JSON settled);
//   3. <html lang> matches the route's language — LanguageSync flips it on
//      /es/* only after changeLanguage finished loading ES resources.
// The 15s deadline guarantees the event always fires so one broken page
// can't hang the whole prerender build. No production listener exists for
// this event, so live visitors are unaffected.
function dispatchPrerenderReady() {
  const deadline = performance.now() + 15_000;
  const expectedLang = /^\/es(\/|$)/.test(window.location.pathname) ? "es" : "en";
  const check = () => {
    const root = document.getElementById("root");
    const routeRendered =
      !!root && root.children.length > 0 && !root.querySelector("[data-route-loader]");
    // i18next's backendConnector keeps in-flight namespace loads on `queue`
    // (each job flagged `done` when its locale JSONs resolve/fail).
    const connector = (i18n.services as { backendConnector?: { queue?: Array<{ done?: boolean }> } })
      .backendConnector;
    const i18nSettled =
      i18n.isInitialized && (connector?.queue ?? []).every((q) => q.done);
    const langApplied = document.documentElement.lang === expectedLang;
    if ((routeRendered && i18nSettled && langApplied) || performance.now() > deadline) {
      // Two extra frames so helmet-managed <head> tags (title/meta/JSON-LD)
      // flush their deferred update before the snapshot.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => document.dispatchEvent(new Event("prerender-ready")))
      );
    } else {
      requestAnimationFrame(check);
    }
  };
  requestAnimationFrame(check);
}
dispatchPrerenderReady();
